import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box, IconButton, TableSortLabel, TextField } from '@mui/material';
import { removeAccentuation } from '../../utils/accentuation';
import { shortDescObjectByKey } from '../../utils/short';
import MultipleSelectChip from './forms/multipleselect';
import DownloadIcon from '@mui/icons-material/Download';
interface  TableHeaderItem {
    id:string,label:string,allowSort?:boolean,align?:'left'|'center'|'justify',
    showValue?:(i:any)=>any,hidden?:boolean,
    hiddenCsv?:boolean,
}

// True se o valor (string/número) contém o termo já normalizado.
function valueMatchesSearch(value:any,normalizedSearch:string){
    if(value===null||value===undefined)return false;
    if(typeof value!=='string'&&typeof value!=='number')return false;
    return removeAccentuation(String(value)).toLocaleLowerCase().indexOf(normalizedSearch)>-1;
}

// Procura o termo em cada coluna usando o valor CRU do campo (`i[h.id]`) — e não
// o `showValue`, que costuma devolver um elemento React (ex.: <CopyField/>) e por
// isso não era pesquisável. Também considera o `showValue` quando ele devolve uma
// string (datas formatadas, etc.).
function rowMatchesSearch(row:any,header:Array<TableHeaderItem>,normalizedSearch:string){
    for(const h of header){
        if(valueMatchesSearch(row[h.id],normalizedSearch))
            return true;
        if(h.showValue){
            const shown=h.showValue(row);
            if(typeof shown==='string'&&valueMatchesSearch(shown,normalizedSearch))
                return true;
        }
    }
    return false;
}

export function TableWithSearchAndSortEngine({header,items,filters,allowDownloadCsv}:{allowDownloadCsv?:boolean,items:Array<any>,header:Array<TableHeaderItem>,filters?:Array<{label:string,filter:(i:any)=>boolean}>}){
    const [searchString,setSearchString]=React.useState("");
    const [sortItem,setSortItem]=React.useState<false|{id:string,direction:'asc'|'desc'}>(false);
    const [selectedItems,setSelectedItems]=React.useState(items);
    const [usedFilters,setUsedFilters]=React.useState<Array<string>>([]);
    React.useEffect(()=>{
       let sItems=filter(items);
       sItems=search(sItems);
       let sorted=getSortArray(sItems);
       if(sorted){
         sItems=sorted;
       }
       setSelectedItems(sItems);
    },[searchString,items,usedFilters]);
    React.useEffect(()=>{
      const sorted=getSortArray(selectedItems);
      if(sorted){
        setSelectedItems(sorted);
      }
    },[sortItem]);
    return (
        <Paper style={{padding:'5px',paddingTop:'12px'}}>
             <div style={{marginBottom:'20px'}}>
             <TextField label='Pesquisar' value={searchString} onChange={(e)=>setSearchString(e.target.value)} />
             {filters?<div style={{marginBottom:'15px'}}>
                 <MultipleSelectChip label="Filtros" onChange={(d)=>{setUsedFilters(d)}} items={filters.map((i)=>{return {label:i.label,value:i.label}})}/>
             </div>:null}
             {allowDownloadCsv?<div>
                 <IconButton onClick={()=>{
                    let csvData='';
                    for(let h of header){
                       if(h.hiddenCsv)continue;
                       csvData+=`${h.id};`
                    }
                    csvData=csvData.slice(0,csvData.length-1);
                    csvData+='\n';
                    for(let item of items){
                        for(let h of header){
                            if(h.hiddenCsv)continue;
                            if(!item[h.id])continue;
                            csvData+=`${item[h.id]};`;
                        }
                        csvData=csvData.slice(0,csvData.length-1);
                        csvData+='\n';
                    }
                    downloadCsv(csvData);
                 }}><DownloadIcon/></IconButton>
             </div>:null}
             </div>
             <GeralTable onChangeSort={(i)=>setSortItem(i)} header={header} items={selectedItems}/>
        </Paper>
    )
    function getSortArray(i:Array<any>){
        if(i.length<1)
            return ;
          if(!sortItem)
              return;
         let obj=shortDescObjectByKey(i,sortItem.id);
         if(sortItem.direction=='asc'){
            obj.reverse()
         }
         return obj;
    }
    function search(sItems:Array<any>){
        if(!searchString)
            return sItems.concat();
        const useSearch=removeAccentuation(searchString.toLocaleLowerCase());
        return sItems.filter((i)=>rowMatchesSearch(i,header,useSearch));
    }
    function filter(sItems:Array<any>){
        if(usedFilters.length==0||!filters)
              return sItems;
        let retItems:Array<any>=[];
        retItems=retItems.concat(sItems);    
        for(let fil of usedFilters){
             let func:any=undefined;
             for(let fitems of filters){
                if(fitems.label==fil){
                    func=fitems.filter;
                }
                if(func)
                     break;
             }
             if(!func)
                 continue;
            let finded:Array<any>=[];
            for(let i=0;i<retItems.length;i++){
                if(!func(retItems[i]))
                    continue;
                finded.push(retItems[i]);
            }          
            retItems=finded;      
        }
        return retItems;    
    }
}


export function GeralTable({header,items,onChangeSort}:{items:Array<any>,header:Array<TableHeaderItem>,onChangeSort?:(i:false|{id:string,direction:'asc'|'desc'})=>void}) {
    const [page,setPage]=React.useState(0); 
    const [rowPerPage,setRowPerPage]=React.useState(5);
    const [sortItem,setSortItem]=React.useState<false|{id:string,direction:'asc'|'desc'}>(false);
    React.useEffect(()=>{
       if(!onChangeSort)
        return
      onChangeSort(sortItem)
    },[sortItem])
    // Quando a lista muda (busca/filtro/ordenação), volta para a primeira página —
    // senão o usuário pode ficar numa página fora do range e ver a tabela vazia.
    React.useEffect(()=>{
       setPage(0);
    },[items])
    return (
        <Box>
        <TableContainer sx={{}} component={Paper}>
            <Table>
            <TableHead >
                 <TableRow>
                    {header.map((v)=>{
                         if(v.hidden)return null;
                         return <TableCell align={v.align}>
                             {v.allowSort?<TableSortLabel direction={sortItem?sortItem.direction:undefined} active={sortItem&&sortItem.id==v.id} onClick={(e)=>{
                                if(sortItem&&sortItem.id==v.id){
                                    if(sortItem.direction=='desc'){
                                        setSortItem(false);
                                    }else{
                                        setSortItem({
                                            id:v.id,
                                            direction:'desc'
                                        })
                                    }
                                }else{
                                    setSortItem({
                                        id:v.id,
                                        direction:'asc'
                                    });
                                }
                             }}>{v.label}</TableSortLabel>:`${v.label}`}
                            </TableCell>
                    })}
                 </TableRow>
            </TableHead>
            <TableBody>
                  {items.slice((page)*rowPerPage,(page+1)*rowPerPage).map((i)=>{
                    return <TableRow>
                         {header.map(v=>{
                            if(v.hidden)return;
                            return(<TableCell>
                             {v.showValue?v.showValue(i):i[v.id]}
                         </TableCell>)
                         })}
                    </TableRow>
                  })}
            </TableBody>
            </Table>
            </TableContainer>
            <TablePagination
                labelRowsPerPage='Itens por página'
                rowsPerPageOptions={[5,10,25,100]}
                page={page}
                count={items.length}
                component="div"
                rowsPerPage={rowPerPage}
                onPageChange={(e,page)=>{setPage(page)}}
                onRowsPerPageChange={(e)=>{setRowPerPage(parseInt(e.target.value))}}
             />
           </Box>
     )
}



function downloadCsv(text:string){
   
const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
const url = URL.createObjectURL(blob);

const a = document.createElement('a');
a.href = url;
a.download = `dados_${(new Date()).toString()}.csv`;
a.click();

URL.revokeObjectURL(url);
}